import React, { useState, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './TierPyramidPopover.css';

function parentCell(cell) {
  const m = String(cell).match(/^(\d+)-(\d+)$/);
  if (!m) return null;
  const k = parseInt(m[1], 10);
  const n = parseInt(m[2], 10);
  if (k <= 1) return null;
  return `${k - 1}-${Math.ceil(n / 2)}`;
}

/** Ordered from apex (1-1) down to `cell`. */
function pathFromApexToCell(cell) {
  if (!cell || !/^\d+-\d+$/.test(String(cell))) return [];
  const chain = [];
  let cur = cell;
  while (cur) {
    chain.push(cur);
    cur = parentCell(cur);
  }
  chain.reverse();
  return chain;
}

function tierLabelFromCell(tierCell) {
  if (!tierCell || typeof tierCell !== 'string') return null;
  const [tier, division] = tierCell.split('-');
  if (!tier || !division) return null;
  return `Tier ${tier}, Division ${division}`;
}

function PyramidGlyphIcon() {
  return (
    <svg className="tier-pyramid-popover-icon-svg" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3 20 20H4L12 3z M8.5 14h7"
      />
    </svg>
  );
}

const SPLIT_TOTAL = 4;

/**
 * Opens a modal explaining the user's position on the division ladder (tier pyramid).
 * Same interaction pattern as MatchLeagueTablePopover: icon trigger, portaled centered panel, backdrop closes.
 */
const TierPyramidPopover = ({
  tierCell,
  tierSplitIndex,
  competitionName,
  tierSeasonRounds
}) => {
  const [open, setOpen] = useState(false);
  const [dockStyle, setDockStyle] = useState(null);
  const anchorRef = useRef(null);

  const path = pathFromApexToCell(tierCell);
  const youLabel = tierLabelFromCell(tierCell);
  const splitLabel =
    tierSplitIndex != null && Number.isFinite(Number(tierSplitIndex))
      ? `Split ${tierSplitIndex} of ${SPLIT_TOTAL}`
      : null;

  const updateDockPosition = useCallback(() => {
    if (!open || !anchorRef.current) return;
    const anchorEl = anchorRef.current;
    const main = anchorEl.closest('.main-content');
    const col = main?.getBoundingClientRect();
    const width = col ? col.width : Math.min(window.innerWidth - 24, 480);
    const centerLeft = col ? col.left + col.width / 2 : window.innerWidth / 2;
    const top = window.innerHeight / 2;
    const maxHeight = Math.min(window.innerHeight * 0.85, window.innerHeight - 32);
    setDockStyle({
      left: centerLeft,
      width,
      top,
      maxHeight,
      transform: 'translate(-50%, -50%)'
    });
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setDockStyle(null);
      return undefined;
    }
    updateDockPosition();
    window.addEventListener('scroll', updateDockPosition, true);
    window.addEventListener('resize', updateDockPosition);
    return () => {
      window.removeEventListener('scroll', updateDockPosition, true);
      window.removeEventListener('resize', updateDockPosition);
    };
  }, [open, updateDockPosition, path.length]);

  if (!tierCell || path.length === 0) return null;

  const closePanel = () => setOpen(false);

  const title = competitionName ? `${competitionName} — division ladder` : 'Division ladder';

  const portalContent = open && dockStyle && (
    <>
      <button
        type="button"
        className="tier-pyramid-popover__backdrop"
        aria-label="Close pyramid map"
        onClick={closePanel}
      />
      <div
        className="tier-pyramid-popover tier-pyramid-popover--docked"
        style={{
          position: 'fixed',
          left: dockStyle.left,
          width: dockStyle.width,
          top: dockStyle.top,
          maxHeight: dockStyle.maxHeight,
          transform: dockStyle.transform,
          zIndex: 200
        }}
        role="dialog"
        aria-label={title}
        onClick={closePanel}
      >
        <div className="tier-pyramid-popover__surface" onClick={(e) => e.stopPropagation()}>
        <div className="tier-pyramid-popover__head">
          <div className="tier-pyramid-popover__head-main">
            <span className="tier-pyramid-popover__title">{title}</span>
            {(splitLabel || youLabel) && (
              <span className="tier-pyramid-popover__sub">
                {[youLabel, splitLabel].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <button
            type="button"
            className="tier-pyramid-popover__close"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              closePanel();
            }}
          >
            ×
          </button>
        </div>
        <div className="tier-pyramid-popover__body">
          <p className="tier-pyramid-popover__explain">
            Higher tiers are closer to the top of the pyramid (one champion cell at tier 1). Each tier splits into two
            divisions below; your cell shows where this mini-league sits on that tree for the current season split.
          </p>
          {tierSeasonRounds && tierSeasonRounds.total ? (
            <p className="tier-pyramid-popover__rounds">
              This split: round <strong>{tierSeasonRounds.current}</strong> of <strong>{tierSeasonRounds.total}</strong>{' '}
              for ladder clashes in this cell.
            </p>
          ) : null}
          <div className="tier-pyramid-popover__pyramid" aria-label="Path from pyramid top to your league">
            {path.map((cell, i) => {
              const isApex = i === 0;
              const isYou = i === path.length - 1;
              const [tk] = cell.split('-');
              return (
                <div key={cell} className="tier-pyramid-popover__step">
                  <div className="tier-pyramid-popover__step-inner">
                    <div
                      className={[
                        'tier-pyramid-popover__node',
                        isYou ? 'tier-pyramid-popover__node--current' : '',
                        isApex ? 'tier-pyramid-popover__node--apex' : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className="tier-pyramid-popover__node-cell">{cell}</span>
                      <span className="tier-pyramid-popover__node-meta">
                        Tier {tk}
                        {isApex ? ' · top' : ''}
                        {isYou ? ' · your league' : ''}
                      </span>
                    </div>
                  </div>
                  {i < path.length - 1 ? (
                    <div className="tier-pyramid-popover__connector" aria-hidden="true">
                      <span className="tier-pyramid-popover__connector-line" />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div ref={anchorRef} className="tier-pyramid-popover-anchor">
        <button
          type="button"
          className="tier-pyramid-popover-trigger"
          title="See where this league sits on the division pyramid (from top flight down to your cell)"
          aria-label="Show division ladder pyramid — where this league sits from top to your tier"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen((o) => !o);
          }}
        >
          <PyramidGlyphIcon />
        </button>
      </div>
      {portalContent && typeof document !== 'undefined' && document.body
        ? ReactDOM.createPortal(portalContent, document.body)
        : null}
    </>
  );
};

export default TierPyramidPopover;
